import React, {type RefObject} from 'react';

interface CheckProps {
    /**
     * A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the CheckIcon element.
     */
    width?: number | string;
}

/**
 * The `Check` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `CheckProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the CheckIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the CheckIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the CheckIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const CheckComponent = <Check className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Check = ({
    className = '',
    color1 = '#0019A5',
    height = 10,
    ref,
    testId = 'Check',
    width = 10
}: CheckProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 10 10"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <g clipPath="url(#clip0_705_1339)" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M8.33268 2.5L3.74935 7.08333L1.66602 5"
                fill="white"
                stroke={color1}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
            />
        </g>
    </svg>
);

Check.displayName = 'Check';